import { FC, ReactNode, useEffect, useRef, useState } from "react";
import _ from "lodash";
import moment from "moment";

import { Callout, Icon } from "@/components";
import {
    Badge,
    Button as DsButton,
    Datepicker,
    Heading,
    IconButton,
    Input,
    Select,
    Text,
    Textarea,
} from "@/ui";
import { useValidator, useForm } from "@/hooks";
import { currencyFormat, timeWindowFormat } from "@/utils";
import { IDataPaneProps, UpdateOrderDetailsInput } from "@/types";
import OrdersItemTable from "./OrdersItemTable";
import { PAYMENT_METHOD, DELIVERY_WINDOW_HOURS, NOT_AVAILABLE } from "@/config";
import { ProductBarcodes } from "./ProductBarcodes";
import { useLineItems } from "./hooks/useLineItems";
import { useUpdateOrderDetails } from "./hooks";
import { useHistory } from "react-router-dom";

import { AddLineItemForm } from "./AddLineItemForm";
import routes from "config/routes/routes";
import { ApolloError } from "apollo-client";
import { OrderPaneLoading } from "./OrderPaneLoading";
import { getOrderStatusTone } from "../../orderVisualTokens";
import badgeStyles from "../../orderBadges.module.scss";
import styles from "./OrderDataPane.module.scss";

const fieldError = (validator, name: string) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const toSelectOptions = (
    options: { value?: string; label?: string }[] = [],
) => {
    const seen = new Set<string>();

    return options
        .filter(option => option?.value != null && option.value !== "")
        .map(option => ({
            value: String(option.value),
            label: option.label ?? String(option.value),
        }))
        .filter(option => {
            if (seen.has(option.value)) return false;
            seen.add(option.value);
            return true;
        });
};

const DetailField = ({
    label,
    children,
}: {
    label: string;
    children: ReactNode;
}) => (
    <div className={styles.field}>
        <Text as="span" className={styles.fieldLabel} variant="body-md">
            {label}
        </Text>
        <div className={styles.fieldValue}>{children}</div>
    </div>
);

const PlusIcon = () => <Icon icon="plus" />;
const TrashIcon = () => <Icon icon="delete" />;

const OrderDataPane: FC<IDataPaneProps> = ({
    loading,
    order,
    account,
    idpAccount,
    namesProductsPOS,
    productNamesStatusMessage,
}) => {
    const history = useHistory();

    const [showEditForm, setShowEditForm] = useState(false);
    const [selection, setSelection] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const topPaneRef = useRef<HTMLDivElement>();
    const showButtons = order?.status === "PENDING";
    const isEditable = order?.status === "PENDING" && showEditForm;

    const scrollToTop = () => {
        topPaneRef.current?.scrollIntoView({
            block: "nearest",
            behavior: "smooth",
        });
    };

    const {
        lineItems,
        setLineItems,
        addLineItemQuantity,
        subLineItemQuantity,
        removeLineItems,
        addLineItem,
    } = useLineItems(order?.line_items);

    const { updateOrderDetails, isUpdating } = useUpdateOrderDetails(
        order?.entity_id,
        () => {
            setSuccess("Order updated successfully");
            scrollToTop();
            setError(null);
            setShowEditForm(false);
            setInputs({ ...inputs, promo_code: "" });

            new Promise(r => setTimeout(r, 3000)).then(() => {
                setSuccess(null);
            });
        },
        (error: ApolloError) => {
            setSuccess(null);
            setError(error?.message);
            scrollToTop();
        },
    );

    const { inputs, setInputs, inputChange, selectChange, genericChange } =
        useForm();

    useEffect(() => {
        setInputs({
            id: order?.entity_id,
            address: order?.delivery_address,
            line_items: lineItems,
            payment_method: order?.payment_details?.type,
            time_window: {
                deliver_after: order?.delivery_details?.deliver_after,
                time_window: order?.delivery_details?.time_window,
            },
            notes: order?.notes,
        } as UpdateOrderDetailsInput);
    }, [order, lineItems]);

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "payment_method",
                method: "isEmpty",
                validWhen: false,
                message: "This payment method is required.",
            },
            {
                field: "address.address1",
                method: "isEmpty",
                validWhen: false,
                message: "This address is required.",
            },
            {
                field: "address.city",
                method: "isEmpty",
                validWhen: false,
                message: "This city is required.",
            },
            {
                field: "address.zip",
                method: "isEmpty",
                validWhen: false,
                message: "This zip is required.",
            },
            {
                field: "address.phone",
                method: "isEmpty",
                validWhen: false,
                message: "This phone is required.",
            },
        ],
        {
            ...inputs,
            ...inputs.delivery_address,
            ...inputs.payment_details,
        },
    );

    const resetFormFromOrder = () => {
        setInputs({
            id: order?.entity_id,
            address: _.cloneDeep(order?.delivery_address) ?? {},
            line_items: order?.line_items,
            payment_method: order?.payment_details?.type,
            time_window: {
                deliver_after: order?.delivery_details?.deliver_after,
                time_window: order?.delivery_details?.time_window,
            },
            notes: order?.notes,
            promo_code: "",
        } as UpdateOrderDetailsInput);
        setLineItems(order?.line_items);
        isSubmited(false);
    };

    const handleSaveOrder = () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (!isValid()) {
            return;
        }

        updateOrderDetails(inputs);
    };

    const handleCancel = () => {
        setError(null);
        setSuccess(null);
        setShowAddForm(false);
        setShowEditForm(false);
        setSelection([]);
        resetFormFromOrder();
    };

    const handleStartEdit = () => {
        setError(null);
        setSuccess(null);
        resetFormFromOrder();
        setShowEditForm(true);
    };

    const orderType = order?.delivery_details?.pickup ? "Pickup" : "Delivery";

    const handleRemoveLineItems = () => {
        removeLineItems(selection);
        setSelection([]);
    };

    const [showAddForm, setShowAddForm] = useState(false);

    const handleAddItem = () => {
        setShowAddForm(true);
    };

    const handleAccountDetian = () => {
        const url = _.replace(
            routes.CUSTOMER_DETAIL.route,
            ":id",
            account?.entity_id,
        );

        history.push(url);
    };

    const renderVerifyStatus = () => {
        const verified = Boolean(idpAccount?.berbix_verified);

        return (
            <Badge
                className={badgeStyles.statusBadge}
                data-tone={verified ? "success" : "error"}
                variant="neutral"
            >
                {verified
                    ? "DRIVER'S LICENSE VERIFIED"
                    : "DRIVER'S LICENSE NOT VERIFIED"}
            </Badge>
        );
    };

    const isLoading = loading;

    if (isLoading) {
        return <OrderPaneLoading />;
    }

    const handleRemoveDiscountItem = promoCode => {
        const _discountItems = _.cloneDeep(order?.discount_items);

        const newDiscountItems = _discountItems.filter(
            discount => discount.promo_code != promoCode,
        );

        setInputs({ ...inputs, discount_items: newDiscountItems });
    };

    const customerName = `${order?.delivery_address?.first_name || ""} ${
        order?.delivery_address?.last_name || ""
    }`.trim();

    const orderTitle = `#${order.order_number} - ${orderType} (${moment(
        order?.delivery_details?.deliver_after,
    ).format("ddd, MMM Do")}, ${timeWindowFormat(
        order?.delivery_details?.time_window,
    )})`;

    const forLabel = order?.delivery_details?.pickup
        ? "Pickup for"
        : "Delivery for";
    const locationLabel = `${
        order?.delivery_details?.pickup ? "Pickup" : "Delivery"
    } Location`;

    return (
        <div className={styles.root} ref={topPaneRef}>
            <div className={styles.badges}>
                <Badge
                    className={badgeStyles.statusBadge}
                    data-tone={getOrderStatusTone(order?.status)}
                    variant="neutral"
                >
                    {_.startCase(order?.status)}
                </Badge>
                {renderVerifyStatus()}
            </div>

            <div className={styles.titleRow}>
                <Heading className={styles.title} level={4}>
                    {orderTitle}
                </Heading>
                {showButtons ? (
                    <div className={styles.titleActions}>
                        {!isEditable ? (
                            <DsButton
                                iconLeft={<Icon icon="pencil" />}
                                onClick={handleStartEdit}
                                type="button"
                                variant="secondary"
                            >
                                Edit order
                            </DsButton>
                        ) : (
                            <>
                                <DsButton
                                    disabled={isUpdating}
                                    onClick={handleCancel}
                                    type="button"
                                    variant="secondary"
                                >
                                    Cancel
                                </DsButton>
                                <DsButton
                                    loading={isUpdating}
                                    onClick={handleSaveOrder}
                                    type="button"
                                    variant="primary"
                                >
                                    Save
                                </DsButton>
                            </>
                        )}
                    </div>
                ) : null}
            </div>

            {(error || success) && (
                <div className={styles.feedback}>
                    {error ? (
                        <div
                            className={styles.alert}
                            data-tone="error"
                            role="alert"
                        >
                            <p className={styles.alertText}>{error}</p>
                            <button
                                aria-label="Dismiss error"
                                className={styles.alertDismiss}
                                onClick={() => setError(null)}
                                type="button"
                            >
                                <Icon color="currentColor" icon="cross" />
                            </button>
                        </div>
                    ) : null}
                    {success ? (
                        <div
                            className={styles.alert}
                            data-tone="success"
                            role="status"
                        >
                            <p className={styles.alertText}>{success}</p>
                            <button
                                aria-label="Dismiss success"
                                className={styles.alertDismiss}
                                onClick={() => setSuccess(null)}
                                type="button"
                            >
                                <Icon color="currentColor" icon="cross" />
                            </button>
                        </div>
                    ) : null}
                </div>
            )}

            <div className={styles.columns}>
                <section className={styles.column}>
                    <Heading className={styles.columnTitle} level={6}>
                        {forLabel}
                    </Heading>
                    <div className={styles.fields}>
                        <DetailField label="Name:">
                            <button
                                className={styles.nameLink}
                                onClick={handleAccountDetian}
                                type="button"
                            >
                                {customerName || NOT_AVAILABLE}
                            </button>
                        </DetailField>
                        <DetailField label="Phone:">
                            {isEditable ? (
                                <Input
                                    error={fieldError(
                                        validator,
                                        "address.phone",
                                    )}
                                    fullWidth
                                    label="Phone"
                                    name="address.phone"
                                    onChange={inputChange}
                                    value={inputs.address?.phone ?? ""}
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {order?.delivery_address?.phone ||
                                        NOT_AVAILABLE}
                                </Text>
                            )}
                        </DetailField>
                        <DetailField label="Email:">
                            <Text as="span" variant="body-md">
                                {order?.email || NOT_AVAILABLE}
                            </Text>
                        </DetailField>
                        <DetailField label="Payment method:">
                            {isEditable ? (
                                <Select
                                    error={fieldError(
                                        validator,
                                        "payment_method",
                                    )}
                                    fullWidth
                                    label="Payment method"
                                    onValueChange={value =>
                                        selectChange(
                                            { value },
                                            "payment_method",
                                        )
                                    }
                                    options={toSelectOptions(PAYMENT_METHOD)}
                                    value={
                                        inputs.payment_method
                                            ? String(inputs.payment_method)
                                            : undefined
                                    }
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {_.startCase(
                                        order?.payment_details?.type,
                                    ) || NOT_AVAILABLE}
                                </Text>
                            )}
                        </DetailField>
                        <DetailField label="Verified status:">
                            {renderVerifyStatus()}
                        </DetailField>
                        <DetailField label="Device:">
                            <Text as="span" variant="body-md">
                                {order?.client_detail?.device || NOT_AVAILABLE}
                            </Text>
                        </DetailField>
                        <DetailField label="Delivery window:">
                            {isEditable ? (
                                <div className={styles.windowEdit}>
                                    <div className={styles.windowEditItem}>
                                        <Datepicker
                                            fullWidth
                                            label="Date"
                                            onChange={date =>
                                                genericChange(
                                                    date
                                                        ? moment(date)
                                                        : undefined,
                                                    "time_window.deliver_after",
                                                )
                                            }
                                            value={
                                                inputs?.time_window
                                                    ?.deliver_after
                                                    ? moment(
                                                          inputs.time_window
                                                              .deliver_after,
                                                      ).toDate()
                                                    : null
                                            }
                                        />
                                    </div>
                                    <div className={styles.windowEditItem}>
                                        <Select
                                            fullWidth
                                            label="Time slot"
                                            onValueChange={value =>
                                                selectChange(
                                                    { value },
                                                    "time_window.time_window",
                                                )
                                            }
                                            options={toSelectOptions([
                                                ...DELIVERY_WINDOW_HOURS,
                                                {
                                                    value: order
                                                        ?.delivery_details
                                                        ?.time_window,
                                                    label: timeWindowFormat(
                                                        order?.delivery_details
                                                            ?.time_window,
                                                    ),
                                                },
                                            ])}
                                            value={
                                                inputs.time_window?.time_window
                                                    ? String(
                                                          inputs.time_window
                                                              .time_window,
                                                      )
                                                    : undefined
                                            }
                                        />
                                    </div>
                                </div>
                            ) : (
                                <Text as="span" variant="body-md">
                                    {`${moment(
                                        order.delivery_details.deliver_after,
                                    ).format("MMMM Do")}${
                                        order.delivery_details.time_window
                                            ? `, ${timeWindowFormat(
                                                  order.delivery_details
                                                      .time_window,
                                              )}`
                                            : ""
                                    }`}
                                </Text>
                            )}
                        </DetailField>
                        <DetailField label="Customer Notes:">
                            {isEditable ? (
                                <Textarea
                                    fullWidth
                                    label="Customer notes"
                                    name="notes"
                                    onChange={inputChange}
                                    rows={5}
                                    value={inputs?.notes || ""}
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {order?.notes || ""}
                                </Text>
                            )}
                        </DetailField>
                    </div>
                </section>

                <section className={styles.column}>
                    <Heading className={styles.columnTitle} level={6}>
                        {locationLabel}
                    </Heading>
                    <div className={styles.fields}>
                        <DetailField label="Address line 1:">
                            {isEditable ? (
                                <Input
                                    error={fieldError(
                                        validator,
                                        "address.address1",
                                    )}
                                    fullWidth
                                    label="Address line 1"
                                    name="address.address1"
                                    onChange={inputChange}
                                    value={inputs.address?.address1 ?? ""}
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {order?.delivery_address?.address1 ||
                                        NOT_AVAILABLE}
                                </Text>
                            )}
                        </DetailField>
                        <DetailField label="Address line 2:">
                            {isEditable ? (
                                <Input
                                    fullWidth
                                    label="Address line 2"
                                    name="address.address2"
                                    onChange={inputChange}
                                    value={inputs.address?.address2 ?? ""}
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {order?.delivery_address?.address2 ||
                                        NOT_AVAILABLE}
                                </Text>
                            )}
                        </DetailField>
                        <DetailField label="City:">
                            {isEditable ? (
                                <Input
                                    error={fieldError(
                                        validator,
                                        "address.city",
                                    )}
                                    fullWidth
                                    label="City"
                                    name="address.city"
                                    onChange={inputChange}
                                    value={inputs.address?.city ?? ""}
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {order?.delivery_address?.city ||
                                        NOT_AVAILABLE}
                                </Text>
                            )}
                        </DetailField>
                        <DetailField label="Zip code:">
                            {isEditable ? (
                                <Input
                                    error={fieldError(validator, "address.zip")}
                                    fullWidth
                                    label="Zip code"
                                    name="address.zip"
                                    onChange={inputChange}
                                    value={inputs.address?.zip ?? ""}
                                />
                            ) : (
                                <Text as="span" variant="body-md">
                                    {order?.delivery_address?.zip ||
                                        NOT_AVAILABLE}
                                </Text>
                            )}
                        </DetailField>
                    </div>
                </section>
            </div>

            <div className={styles.legacyBelow}>
                {order?.status === "PENDING" && (
                    <ProductBarcodes
                        isLoading={isLoading}
                        lineItems={order?.line_items}
                        orderId={order?.entity_id}
                    />
                )}
                <div className={styles.itemsSection}>
                    <Heading className={styles.itemsTitle} level={5}>
                        Items Ordered
                    </Heading>
                    {productNamesStatusMessage ? (
                        <Callout role="status" variant="warning" width="fit">
                            <p className="mb-0">
                                POS product names are unavailable:{" "}
                                {productNamesStatusMessage}
                            </p>
                        </Callout>
                    ) : null}
                    <OrdersItemTable
                        getOrderLoading={isLoading}
                        handleAddItemQuantity={addLineItemQuantity}
                        handleSubItemQuantity={subLineItemQuantity}
                        lineItems={lineItems}
                        loading={loading}
                        namesProductsPOS={namesProductsPOS}
                        selection={selection}
                        setSelection={setSelection}
                        showEditForm={isEditable}
                        updating={false}
                    />
                </div>
                {isEditable && showAddForm ? (
                    <div className={styles.addItemForm}>
                        <AddLineItemForm
                            addLineItem={addLineItem}
                            hideForm={() => {
                                setShowAddForm(false);
                            }}
                            lineItems={order.line_items}
                            providerStoreID={
                                order.jane_store_id || order.provider_store_id
                            }
                        />
                    </div>
                ) : null}
                <div className={styles.summaryBar}>
                    <div className={styles.summaryToolbar}>
                        {isEditable ? (
                            <>
                                <DsButton
                                    disabled={showAddForm}
                                    iconLeft={<PlusIcon />}
                                    onClick={handleAddItem}
                                    type="button"
                                    variant="secondary"
                                >
                                    Add item
                                </DsButton>
                                {selection.length ? (
                                    <DsButton
                                        iconLeft={<TrashIcon />}
                                        onClick={handleRemoveLineItems}
                                        type="button"
                                        variant="secondary"
                                    >
                                        {`Delete item${
                                            selection.length > 1 ? "s" : ""
                                        } (${selection.length})`}
                                    </DsButton>
                                ) : null}
                            </>
                        ) : null}
                    </div>

                    <div className={styles.summary}>
                        {order?.discount_items?.length || isEditable ? (
                            <div className={styles.summaryRow}>
                                <p className={styles.summaryLabel}>
                                    Promo code:
                                </p>
                                <div className={styles.promoField}>
                                    {isEditable ? (
                                        <Input
                                            density="compact"
                                            fullWidth
                                            label="Promo code"
                                            labelBehavior="inline"
                                            name="promo_code"
                                            onChange={inputChange}
                                            placeholder="Promo code"
                                            value={inputs.promo_code || ""}
                                        />
                                    ) : null}
                                    {order?.discount_items?.length ? (
                                        <ul className={styles.promoList}>
                                            {order.discount_items.map(
                                                (discount, index) => (
                                                    <li
                                                        className={
                                                            styles.promoItem
                                                        }
                                                        key={`${discount?.promo_code}-${index}`}
                                                    >
                                                        <Text
                                                            as="span"
                                                            variant="body-md"
                                                        >
                                                            {
                                                                discount.promo_code
                                                            }
                                                        </Text>
                                                        {isEditable ? (
                                                            <IconButton
                                                                aria-label={`Remove promo ${discount?.promo_code}`}
                                                                onClick={() =>
                                                                    handleRemoveDiscountItem(
                                                                        discount?.promo_code,
                                                                    )
                                                                }
                                                                size="sm"
                                                                type="button"
                                                                variant="tertiary"
                                                            >
                                                                <Icon icon="delete" />
                                                            </IconButton>
                                                        ) : null}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        <div className={styles.summaryRow}>
                            <p className={styles.summaryLabel}>Subtotal:</p>
                            <p className={styles.summaryValue}>
                                {currencyFormat(order.subtotal_price)}
                            </p>
                        </div>
                        <div className={styles.summaryRow}>
                            <p className={styles.summaryLabel}>Delivery fee:</p>
                            <p className={styles.summaryValue}>
                                {currencyFormat(order.total_delivery)}
                            </p>
                        </div>
                        <div className={styles.summaryRow}>
                            <p className={styles.summaryLabel}>Service fee:</p>
                            <p className={styles.summaryValue}>
                                {currencyFormat(order.total_service_fee)}
                            </p>
                        </div>
                        <div className={styles.summaryRow}>
                            <p className={styles.summaryLabel}>Tax:</p>
                            <p className={styles.summaryValue}>
                                {_.get(order, "total_tax", 0) > 0
                                    ? currencyFormat(order.total_tax)
                                    : "Included"}
                            </p>
                        </div>
                        {_.get(order, "total_discounts", 0) > 0 ? (
                            <div
                                className={styles.summaryRow}
                                data-tone="discount"
                            >
                                <p className={styles.summaryLabel}>
                                    Discounts:
                                </p>
                                <p className={styles.summaryValue}>
                                    {`-${currencyFormat(
                                        order.total_discounts,
                                    )}`}
                                </p>
                            </div>
                        ) : null}
                        <div className={styles.summaryRow} data-tone="total">
                            <p className={styles.summaryLabel}>Total:</p>
                            <p className={styles.summaryValue}>
                                {currencyFormat(order.total_price)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDataPane;
